/** biome-ignore-all lint/style/noRestrictedImports: date-fns imports are allowed in @cobliteam/utils only. All other packages must import from @cobliteam/utils/date. */
import { type FormatDistanceToNowOptions, format, formatDistanceToNow, formatRelative } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';
import { DEFAULT_LOCALE, DEFAULT_TZ } from './constants';
import { toDate } from './helpers';
import type { DateValue } from './types';

/**
 * Formats a date relative to a base date (default: now), with custom separator and timezone.
 * Example outputs:
 *   - "Hoje às 10:00"
 *   - "Ontem às 17:00"
 *   - "11/11/2025 às 09:00"
 */
export function formatRelativeDate(date: DateValue, baseDate: DateValue = new Date(), separator = 'às', timezone: string = DEFAULT_TZ): string {
  const dateObj = toZonedTime(toDate(date), timezone);
  const baseObj = toZonedTime(toDate(baseDate), timezone);

  const relative = formatRelative(dateObj, baseObj, {
    locale: DEFAULT_LOCALE,
  });

  const withTime = relative.includes('às') ? relative.replace('às', separator) : `${relative} ${separator} ${format(dateObj, 'HH:mm')}`;

  return withTime.charAt(0).toUpperCase() + withTime.slice(1);
}

/**
 * Formats a date relative to now, with custom separator and timezone.
 * Example outputs:
 *   - "Hoje às 10:00"
 *   - "Ontem às 17:00"
 *   - "11/11/2025 às 09:00"
 */
export function formatRelativeDateToNow(date: DateValue, options?: FormatDistanceToNowOptions): string {
  const dateObj = toZonedTime(toDate(date), DEFAULT_TZ);
  return formatDistanceToNow(dateObj, { locale: ptBR, ...options });
}
